import { useEffect, useState } from 'react';
import { RefreshCw, Server, ShieldAlert, ShieldCheck, TrendingUp } from 'lucide-react';
import {
  Button,
  Card,
  NotificationCard,
  StatusBadge,
  UniversalPage,
} from '@afrilink/ui';
import {
  adminAccountsService,
  type WhmServerHealthRow,
} from '@/domains/hosting/services/adminAccountsService';
import {
  resellerCapService,
  type ResellerCapSnapshot,
} from '@/domains/hosting/services/resellerCap';
import {
  customerGrowthService,
  type GrowthProjection,
} from '@/domains/hosting/services/customerGrowth';
import {
  RESELLER_SUBSTRATE,
  costPerCustomerZar,
  customerMilestoneBracket,
  getStalenessStatus,
} from '@/config/resellerSubstrate';
import { ColoFinancialModel } from '@/apps/admin/components/ColoFinancialModel';

function formatRelative(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
}

function formatGb(mb: number | null | undefined): string {
  if (mb === null || mb === undefined) return '—';
  if (mb === -1) return '∞';
  return `${(mb / 1024).toFixed(1)} GB`;
}

function formatZar(n: number): string {
  return `R${n.toLocaleString('en-ZA', { maximumFractionDigits: 2 })}`;
}

export default function AdminServerHealthPage() {
  const [rows, setRows] = useState<WhmServerHealthRow[]>([]);
  const [cap, setCap] = useState<ResellerCapSnapshot | null>(null);
  const [growth, setGrowth] = useState<GrowthProjection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadAll = () => {
    adminAccountsService.getServerHealth().then(setRows).catch((e: unknown) => {
      setError(e instanceof Error ? e.message : String(e));
    });
    resellerCapService
      .loadLatest()
      .then(setCap)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
    customerGrowthService
      .getProjection()
      .then(setGrowth)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleRefreshSnapshot = async () => {
    setRefreshing(true);
    const result = await resellerCapService.refreshNow();
    setRefreshing(false);
    if ('error' in result && !result.ok) {
      setError(`Refresh failed: ${result.error}`);
      return;
    }
    // Reload from DB once the snapshot is upserted
    loadAll();
  };

  const staleness = getStalenessStatus();

  return (
    <UniversalPage
      title="Server health & reseller capacity"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Hosting', href: '/admin/hosting' },
        { label: 'Server health & capacity' },
      ]}
    >
      {error && <NotificationCard tone="danger" title="Could not load" body={error} />}

      {staleness.isStale && (
        <NotificationCard
          tone="warning"
          title="Substrate constants stale"
          body={`Reseller cost figures were last verified ${staleness.daysSinceVerified} days ago. Re-verify against domains.co.za pricing and bump lastVerifiedAt in resellerSubstrate.ts.`}
        />
      )}

      <Card interactive={false} className="border-info/30 bg-info/5 p-4 text-sm">
        <p>
          One row per WHM server registered in <code>whm_server</code>. Capacity utilisation is
          computed from <code>customer_link</code>; failure count is jobs in{' '}
          <code>failed_terminal</code> / <code>failed_retryable</code> in the last 24h. Reseller
          capacity sections below read from daily <code>usage_snapshot</code> rollups (cron at
          01:30 UTC / 03:30 SAST) — Play 13 disk monitor + Play 16 co-lo tracker.
        </p>
      </Card>

      {/* ============================================================== */}
      {/* Existing per-server health rows (unchanged behaviour)          */}
      {/* ============================================================== */}
      <div className="space-y-3">
        {rows.map((r) => {
          const capMax = r.capacity_max_accounts;
          const used = r.active_accounts;
          const pct = capMax && capMax > 0 ? (used / capMax) * 100 : null;
          const tone =
            pct === null
              ? 'muted'
              : pct > 90
                ? 'danger'
                : pct > 75
                  ? 'warning'
                  : 'ok';
          return (
            <Card key={r.hostname} interactive={false}>
              <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
                <div className="flex items-center gap-3">
                  <Server className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">{r.hostname}</p>
                    <p className="text-xs text-muted-foreground">
                      port {r.port} · api_user <code className="font-sans">{r.api_user}</code>
                    </p>
                  </div>
                </div>
                {r.is_active ? (
                  <StatusBadge tone="active">
                    <span className="inline-flex items-center gap-1">
                      <ShieldCheck className="h-3 w-3" /> Active
                    </span>
                  </StatusBadge>
                ) : (
                  <StatusBadge tone="draft">
                    <span className="inline-flex items-center gap-1">
                      <ShieldAlert className="h-3 w-3" /> Inactive
                    </span>
                  </StatusBadge>
                )}
              </header>

              <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-4">
                <Stat label="Active accounts" value={used.toString()} />
                <Stat label="Capacity" value={capMax !== null ? capMax.toString() : '∞'} />
                <Stat
                  label="Utilisation"
                  value={pct !== null ? `${pct.toFixed(1)}%` : '—'}
                  tone={tone}
                />
                <Stat
                  label="Failures (24h)"
                  value={r.failures_24h.toString()}
                  tone={r.failures_24h > 0 ? 'danger' : 'ok'}
                />
              </div>

              {pct !== null && (
                <div className="px-4 pb-4">
                  <ProgressBar pct={pct} tone={tone} />
                </div>
              )}

              <footer className="border-t border-border bg-card-subtle px-4 py-2 text-xs text-muted-foreground">
                Last health check: {formatRelative(r.last_health_check_at)}
              </footer>
            </Card>
          );
        })}
        {rows.length === 0 && !error && (
          <Card interactive={false} className="p-12 text-center text-sm text-muted-foreground">
            No WHM servers registered.
          </Card>
        )}
      </div>

      {/* ============================================================== */}
      {/* Play 13 — Reseller disk capacity                                */}
      {/* ============================================================== */}
      <section aria-labelledby="reseller-cap-heading" className="space-y-3 pt-4">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 id="reseller-cap-heading" className="text-base font-semibold text-foreground">
              Reseller disk capacity
            </h2>
            <p className="text-xs text-muted-foreground">
              {RESELLER_SUBSTRATE.currentPlan.name} · {formatZar(RESELLER_SUBSTRATE.currentPlan.costZarMonthly)}/mo · {formatGb(RESELLER_SUBSTRATE.currentPlan.diskCapMb)} physical cap
            </p>
          </div>
          {cap && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Snapshot: {cap.snapshot_date}{cap.is_stale ? ' (stale)' : ''}</span>
              <Button size="sm" variant="outline" onClick={handleRefreshSnapshot} disabled={refreshing}>
                <RefreshCw className={refreshing ? 'mr-1 h-3 w-3 animate-spin' : 'mr-1 h-3 w-3'} />
                {refreshing ? 'Refreshing…' : 'Refresh now'}
              </Button>
            </div>
          )}
        </header>

        {cap === null ? (
          <Card interactive={false} className="p-8 text-center text-sm text-muted-foreground">
            <p>No usage snapshots yet — first cron run lands at 03:30 SAST.</p>
            <Button size="sm" className="mt-3" onClick={handleRefreshSnapshot} disabled={refreshing}>
              <RefreshCw className={refreshing ? 'mr-1 h-3 w-3 animate-spin' : 'mr-1 h-3 w-3'} />
              Run snapshot now
            </Button>
          </Card>
        ) : (
          <>
            {/* Alarm strip — Sprint C C-2 adds inode breach as a primary signal.
                Inode pressure causes writes to fail while disk gauges look healthy,
                so it must surface in the strip and not just the per-row tinting. */}
            {(cap.alarms.disk_critical
              || cap.alarms.disk_warning
              || cap.alarms.accounts_over_threshold.length > 0
              || cap.alarms.accounts_over_inode_threshold.length > 0) && (
              <NotificationCard
                tone={
                  cap.alarms.disk_critical || cap.alarms.accounts_over_inode_threshold.length > 0
                    ? 'danger'
                    : 'warning'
                }
                title="Capacity alert"
                body={[
                  cap.alarms.disk_critical
                    ? `Reseller disk at ${cap.totals.pct_of_disk_cap.toFixed(0)}% — upgrade tier urgently.`
                    : cap.alarms.disk_warning
                      ? `Reseller disk at ${cap.totals.pct_of_disk_cap.toFixed(0)}% — review accounts.`
                      : '',
                  cap.alarms.accounts_over_threshold.length > 0
                    ? `Heavy account(s): ${cap.alarms.accounts_over_threshold.join(', ')}.`
                    : '',
                  cap.alarms.accounts_over_inode_threshold.length > 0
                    ? `Inode pressure >70% on: ${cap.alarms.accounts_over_inode_threshold.join(', ')}. Writes may fail before disk is full — investigate (often runaway mailboxes or cache dirs).`
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              />
            )}

            {/* Cards 1 + 2 + 4 — gauges + overselling */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {/* Card 1 — Reseller disk gauge */}
              <Card interactive={false}>
                <div className="p-4">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Reseller disk used
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-foreground">
                    {formatGb(cap.totals.disk_used_mb)} / {formatGb(RESELLER_SUBSTRATE.currentPlan.diskCapMb)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {cap.totals.pct_of_disk_cap.toFixed(1)}% of physical cap
                  </p>
                </div>
                <div className="px-4 pb-4">
                  <ProgressBar
                    pct={cap.totals.pct_of_disk_cap}
                    tone={
                      cap.totals.pct_of_disk_cap > RESELLER_SUBSTRATE.alarms.diskCriticalPct
                        ? 'danger'
                        : cap.totals.pct_of_disk_cap > RESELLER_SUBSTRATE.alarms.diskWarnPct
                          ? 'warning'
                          : 'ok'
                    }
                  />
                </div>
              </Card>

              {/* Card 2 — Bandwidth gauge (placeholder for Sprint C E.1) */}
              <Card interactive={false}>
                <div className="p-4">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Reseller bandwidth (this month)
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-muted-foreground">
                    — / {formatGb(RESELLER_SUBSTRATE.currentPlan.bwCapMb)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Bandwidth telemetry shipping in Sprint C (WHM <code>showbw</code> integration).
                  </p>
                </div>
              </Card>

              {/* Card 4 — Overselling indicator */}
              <Card interactive={false}>
                <div className="p-4">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Overselling ratio
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-foreground">
                    {cap.totals.overselling_ratio.toFixed(1)}×
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatGb(cap.totals.disk_allocated_mb)} allocated / {formatGb(RESELLER_SUBSTRATE.currentPlan.diskCapMb)} physical. Industry norm: 3:1 to 10:1.
                  </p>
                </div>
              </Card>
            </div>

            {/* Card 3 — Per-account breakdown */}
            <Card interactive={false}>
              <header className="border-b border-border p-4">
                <h3 className="text-sm font-semibold text-foreground">Per-account disk usage</h3>
                <p className="text-xs text-muted-foreground">
                  {cap.accounts.length} active customer{cap.accounts.length === 1 ? '' : 's'}. Sorted by % of reseller cap descending.
                </p>
              </header>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-card-subtle text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 text-left">cPanel user</th>
                      <th className="px-4 py-2 text-left">Primary domain</th>
                      <th className="px-4 py-2 text-right">Used</th>
                      <th className="px-4 py-2 text-right">Quota</th>
                      <th className="px-4 py-2 text-right">% of quota</th>
                      <th className="px-4 py-2 text-right">% of cap</th>
                      <th className="px-4 py-2 text-right" title="Sprint C C-2 — inode usage from WHM accountsummary">Inodes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {cap.accounts.map((a) => {
                      const pctCap = a.pct_of_cap ?? 0;
                      const inodePct = a.pct_of_inode_quota ?? 0;
                      const inodeWarn = inodePct > 70;
                      const rowTone =
                        pctCap > 40 || inodeWarn
                          ? 'bg-danger/5 text-foreground'
                          : pctCap > 20
                            ? 'bg-warning/5 text-foreground'
                            : 'text-foreground';
                      return (
                        <tr key={a.customer_link_id} className={rowTone}>
                          <td className="px-4 py-2 font-mono text-xs">{a.cpanel_username}</td>
                          <td className="px-4 py-2 text-muted-foreground">{a.primary_domain ?? '—'}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{formatGb(a.disk_used_mb)}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{formatGb(a.disk_quota_mb)}</td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            {a.pct_of_quota !== null ? `${a.pct_of_quota.toFixed(1)}%` : '—'}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            {a.pct_of_cap !== null ? `${a.pct_of_cap.toFixed(1)}%` : '—'}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            {a.pct_of_inode_quota !== null ? (
                              <span
                                className={inodeWarn ? 'font-semibold text-danger' : 'text-foreground'}
                                title={
                                  a.inodes_used !== null && a.inodes_quota !== null
                                    ? `${a.inodes_used.toLocaleString()} of ${a.inodes_quota.toLocaleString()}`
                                    : undefined
                                }
                              >
                                {inodeWarn && '⚠️ '}
                                {a.pct_of_inode_quota.toFixed(0)}%
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {cap.accounts.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                          No customer-linked accounts in this snapshot.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}
      </section>

      {/* ============================================================== */}
      {/* Play 16 — Substrate economics + decision triggers              */}
      {/* ============================================================== */}
      <section aria-labelledby="substrate-heading" className="space-y-3 pt-4">
        <header>
          <h2 id="substrate-heading" className="text-base font-semibold text-foreground">
            Substrate economics &amp; decision triggers
          </h2>
          <p className="text-xs text-muted-foreground">
            Milestones for upgrading reseller tier or co-locating. See{' '}
            <code>memory/competitive_analysis_substrate_economics_20260513.md</code>.
          </p>
        </header>

        {growth === null ? (
          <Card interactive={false} className="p-6 text-center text-sm text-muted-foreground">
            Loading growth projection…
          </Card>
        ) : (
          <>
            {!growth.reliable && growth.unreliableReason && (
              <NotificationCard
                tone="warning"
                title="Growth projection unreliable"
                body={`${growth.unreliableReason} Slope shown but dates suppressed.`}
              />
            )}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {/* Card B — Growth trajectory + cliff projection */}
              <Card interactive={false}>
                <header className="flex items-center gap-2 border-b border-border p-4">
                  <TrendingUp className="h-5 w-5 text-muted-foreground" />
                  <h3 className="text-sm font-semibold text-foreground">Growth trajectory</h3>
                </header>
                <dl className="space-y-2 p-4 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Customer growth</dt>
                    <dd className="font-medium tabular-nums">
                      {(growth.customerSlopePerDay * 30).toFixed(1)} / month
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Disk growth</dt>
                    <dd className="font-medium tabular-nums">
                      {((growth.diskSlopeMbPerDay * 30) / 1024).toFixed(2)} GB / month
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Total disk used today</dt>
                    <dd className="font-medium tabular-nums">{formatGb(growth.totalDiskUsedMb)}</dd>
                  </div>
                </dl>
                {growth.reliable && growth.earlierCliffDate && (
                  <div className="border-t border-warning/30 bg-warning/10 px-4 py-3 text-xs">
                    <p className="font-semibold text-warning-dark">
                      ⚠️ Cliff approaches via <em>{growth.earlierCliffDimension}</em>
                    </p>
                    <p className="mt-1 text-warning-dark">
                      Account cap (25): {growth.customerCapDate ?? 'unreachable at current rate'}<br />
                      Disk cap ({formatGb(RESELLER_SUBSTRATE.currentPlan.diskCapMb)}): {growth.diskCapDate ?? 'unreachable at current rate'}<br />
                      <strong>Decision needed by {growth.earlierCliffDate}</strong>
                    </p>
                  </div>
                )}
              </Card>

              {/* Card C — Substrate economics */}
              <Card interactive={false}>
                <header className="border-b border-border p-4">
                  <h3 className="text-sm font-semibold text-foreground">Substrate cost per customer</h3>
                </header>
                <dl className="space-y-2 p-4 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Current ({growth.customerCount} customers)</dt>
                    <dd className="font-semibold tabular-nums text-foreground">
                      {growth.customerCount > 0 ? formatZar(costPerCustomerZar(growth.customerCount)) : '—'}/mo
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">At account cap (25)</dt>
                    <dd className="font-medium tabular-nums">{formatZar(costPerCustomerZar(25))}/mo</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">xneelo benchmark (own metal)</dt>
                    <dd className="font-medium tabular-nums">~{formatZar(RESELLER_SUBSTRATE.competitorBenchmark.xneeloPerCustomerZar)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Afrihost benchmark (own metal, scale)</dt>
                    <dd className="font-medium tabular-nums">~{formatZar(RESELLER_SUBSTRATE.competitorBenchmark.afrihostPerCustomerZar)}</dd>
                  </div>
                </dl>
                <footer className="border-t border-border bg-card-subtle px-4 py-2 text-xs text-muted-foreground">
                  Bracket:{' '}
                  <strong className="text-foreground">
                    {customerMilestoneBracket(growth.customerCount).replace(/_/g, ' ')}
                  </strong>{' '}
                  · Last verified {staleness.verifiedAt}
                </footer>
              </Card>
            </div>

            {/* Card D — Decision-trigger calendar */}
            <Card interactive={false}>
              <header className="border-b border-border p-4">
                <h3 className="text-sm font-semibold text-foreground">Decision-trigger calendar</h3>
                <p className="text-xs text-muted-foreground">
                  Projected dates assuming current customer-onboarding rate continues.
                </p>
              </header>
              <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-3">
                <MilestoneTile
                  label="Plan ahead (18 customers)"
                  date={growth.reliable ? growth.milestones.planAheadDate : null}
                  reliable={growth.reliable}
                  description="Discuss Medium R939 upgrade OR second R419 slot."
                />
                <MilestoneTile
                  label="Decide now (23+ customers)"
                  date={growth.reliable ? growth.milestones.decideNowDate : null}
                  reliable={growth.reliable}
                  description="Must upgrade reseller plan OR add second slot. Hard cap."
                />
                <MilestoneTile
                  label="Co-locate (40+ customers)"
                  date={growth.reliable ? growth.milestones.coLocateDate : null}
                  reliable={growth.reliable}
                  description="Capex + opex justifies own metal. Phase 13 candidate."
                />
              </div>
            </Card>

            {/* Card E — Upgrade-path comparison */}
            <Card interactive={false}>
              <header className="border-b border-border p-4">
                <h3 className="text-sm font-semibold text-foreground">Upgrade-path comparison</h3>
              </header>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-card-subtle text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 text-left">Plan</th>
                      <th className="px-4 py-2 text-right">Cost</th>
                      <th className="px-4 py-2 text-right">Disk cap</th>
                      <th className="px-4 py-2 text-right">Account cap</th>
                      <th className="px-4 py-2 text-right">Cost/customer at cap</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {RESELLER_SUBSTRATE.upgradePaths.map((p) => {
                      const isCoLo = 'isCoLo' in p && p.isCoLo === true;
                      return (
                      <tr
                        key={p.name}
                        className={p.isCurrent ? 'bg-primary/5 font-medium' : ''}
                      >
                        <td className="px-4 py-2">
                          {p.label}
                          {p.isCurrent && <span className="ml-2 text-xs text-primary">CURRENT</span>}
                          {isCoLo && <span className="ml-2 text-xs text-warning">EST</span>}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">{formatZar(p.costZarMonthly)}/mo</td>
                        <td className="px-4 py-2 text-right tabular-nums">{formatGb(p.diskCapMb)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{p.accountCap}</td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {formatZar(p.costZarMonthly / p.accountCap)}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Co-lo financial model */}
            <ColoFinancialModel />
          </>
        )}
      </section>
    </UniversalPage>
  );
}

function Stat({
  label,
  value,
  tone = 'muted',
}: {
  label: string;
  value: string;
  tone?: 'ok' | 'warning' | 'danger' | 'muted';
}) {
  const cls =
    tone === 'danger'
      ? 'text-danger'
      : tone === 'warning'
        ? 'text-warning'
        : tone === 'ok'
          ? 'text-foreground'
          : 'text-muted-foreground';
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 text-lg font-semibold ${cls}`}>{value}</p>
    </div>
  );
}

function ProgressBar({ pct, tone }: { pct: number; tone: 'ok' | 'warning' | 'danger' | 'muted' }) {
  const cls =
    tone === 'danger'
      ? 'bg-danger'
      : tone === 'warning'
        ? 'bg-warning'
        : 'bg-success';
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
      <div className={`h-full transition-all ${cls}`} style={{ width: `${Math.min(pct, 100)}%` }} />
    </div>
  );
}

function MilestoneTile({
  label,
  date,
  reliable,
  description,
}: {
  label: string;
  date: string | null;
  reliable: boolean;
  description: string;
}) {
  return (
    <div className="rounded border border-border bg-card-subtle p-3">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 text-base font-semibold text-foreground">
        {reliable ? (date ?? 'unreachable') : 'data unreliable'}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{description}</p>
    </div>
  );
}
